/**
 * NLCupWatch.tsx — Public landing page for Carmel FC 2013G Gold NL
 * NL Cup National Championship Finals streaming hub.
 *
 * URL: /NLCUP
 * No login required.
 */
import React, { useEffect, useState } from 'react';
import { mediaUrl } from '../utils/mediaUrl';

// ── NL Cup brand colors (from thenationalleague.com) ──────────────────────────
const NL_NAVY  = '#1D2E5F';
const NL_RED   = '#E0193B';
const NL_GOLD  = '#C9963C';
const NL_WHITE = '#ffffff';

// ── Logo URLs ─────────────────────────────────────────────────────────────────
const NL_CUP_LOGO  = '/api/videos/proxy/logos/nl_cup_logo.png';
const USYS_NL_LOGO = '/api/videos/proxy/logos/usys_nl_logo.png';
const CARMEL_LOGO  = '/api/videos/proxy/logos/logo_a9aa2911-a077-417f-abd1-e1c8a714e3b7.jpeg';

// ── Game definitions ──────────────────────────────────────────────────────────
const GAMES = [
  {
    id:           '766188f3-29c1-4a00-b606-a1f3d376ada9',
    label:        'Game 1',
    date:         'Tuesday, July 7',
    time:         '8:00 AM CST / 9:00 AM EST',
    opponent:     'RSC Premier',
    opponentSub:  'Mississippi',
    opponentLogo: '/api/videos/proxy/photos/photo_d6a5f256-8ae4-466a-ac58-43ad84574143.webp',
  },
  {
    id:           '6d6a4094-a2f6-42d1-b41b-05d4d5f67ec4',
    label:        'Game 2',
    date:         'Wednesday, July 8',
    time:         '12:30 PM CST / 1:30 PM EST',
    opponent:     'TSC Murfreesboro',
    opponentSub:  'Tennessee',
    opponentLogo: null,
  },
  {
    id:           'f7b73ac9-1b95-4dd1-a50a-8a5986ab0456',
    label:        'Game 3',
    date:         'Thursday, July 9',
    time:         '10:15 AM CST / 11:15 AM EST',
    opponent:     'MS Rush Premier',
    opponentSub:  'Mississippi',
    opponentLogo: '/api/videos/proxy/photos/photo_bb043780-53a0-4f20-b907-62b90e79500c.webp',
  },
];

interface GameState {
  status: string;
  home_score?: number;
  away_score?: number;
  recording?: {
    id: string;
    file_path: string;
    compressed_path?: string | null;
    title?: string;
    duration_seconds?: number | null;
  } | null;
}

// ── Apple App Store badge ─────────────────────────────────────────────────────
const AppStoreBadge: React.FC = () => (
  <a
    href="https://apps.apple.com/us/app/slow-rib-sports/id6760488944"
    target="_blank"
    rel="noopener noreferrer"
    style={{ display: 'inline-block', lineHeight: 0 }}
  >
    <svg width="140" height="42" viewBox="0 0 140 42" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="140" height="42" rx="7" fill="#000"/>
      <rect width="140" height="42" rx="7" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="0.8"/>
      <path d="M21.5 10.5c1.2-1.4 2-3.3 1.8-5.2-1.7.1-3.8 1.2-5 2.6-1.1 1.2-2 3.2-1.8 5 2 .2 3.9-.9 5-2.4z" fill="white"/>
      <path d="M23.3 13.3c-2.8-.2-5.1 1.6-6.4 1.6-1.3 0-3.3-1.5-5.4-1.5-2.8.1-5.4 1.7-6.8 4.2-2.9 5-.8 12.4 2 16.5 1.4 2 3 4.2 5.2 4.1 2-.1 2.8-1.3 5.2-1.3 2.4 0 3.1 1.3 5.2 1.3 2.2 0 3.7-2 5-4 1.6-2.3 2.2-4.5 2.3-4.6-.1 0-4.4-1.7-4.4-6.6 0-4.2 3.4-6.2 3.6-6.3-2-2.9-5-3.4-5.5-3.4z" fill="white"/>
      <text x="36" y="16" fill="white" fontFamily="-apple-system, Helvetica, Arial, sans-serif" fontSize="8" fontWeight="400" letterSpacing="0.2">Download on the</text>
      <text x="35" y="30" fill="white" fontFamily="-apple-system, Helvetica, Arial, sans-serif" fontSize="17" fontWeight="600" letterSpacing="-0.3">App Store</text>
    </svg>
  </a>
);

// ── Team logo avatar (with initials fallback) ─────────────────────────────────
const TeamLogo: React.FC<{
  src: string | null;
  alt: string;
  size?: number;
  initials?: string;
  bgColor?: string;
  borderColor?: string;
}> = ({ src, alt, size = 56, initials = '?', bgColor = 'rgba(255,255,255,0.08)', borderColor = 'rgba(255,255,255,0.15)' }) => {
  const [errored, setErrored] = useState(false);
  return (
    <div style={{
      width:          size,
      height:         size,
      borderRadius:   12,
      background:     bgColor,
      border:         `1.5px solid ${borderColor}`,
      display:        'flex',
      alignItems:     'center',
      justifyContent: 'center',
      flexShrink:     0,
      overflow:       'hidden',
      boxShadow:      '0 2px 8px rgba(0,0,0,0.3)',
    }}>
      {src && !errored ? (
        <img
          src={mediaUrl(src)}
          alt={alt}
          style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }}
          onError={() => setErrored(true)}
        />
      ) : (
        <span style={{ fontSize: size * 0.27, fontWeight: 800, color: 'rgba(255,255,255,0.45)', letterSpacing: '-0.5px' }}>
          {initials}
        </span>
      )}
    </div>
  );
};

// ── Individual game card ──────────────────────────────────────────────────────
const GameCard: React.FC<{
  game: typeof GAMES[number];
  index: number;
}> = ({ game, index }) => {
  const [state, setState] = useState<GameState | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPlayer, setShowPlayer] = useState(false);

  useEffect(() => {
    const API_BASE = process.env.REACT_APP_API_URL ?? '/api';
    const fetchState = () =>
      fetch(`${API_BASE}/games/${game.id}/public`)
        .then(r => r.ok ? r.json() : null)
        .then((data: any) => {
          if (!data) return;
          setState({
            status:     data.status,
            home_score: data.score?.home_score,
            away_score: data.score?.away_score,
            recording:  data.recording ?? null,
          });
        })
        .catch(() => {})
        .finally(() => setLoading(false));

    fetchState();
    const interval = setInterval(fetchState, 30_000);
    return () => clearInterval(interval);
  }, [game.id]);

  const isLive      = state?.status === 'live';
  const isCompleted = state?.status === 'completed';
  const hasRecording = isCompleted && !!state?.recording?.file_path;

  const recordingUrl = hasRecording ? (() => {
    const HLS_BASE = process.env.REACT_APP_HLS_URL ?? '/hls';
    const fp = state!.recording!.compressed_path || state!.recording!.file_path;
    return (fp.startsWith('/api/') || fp.startsWith('http')) ? fp : `${HLS_BASE}${fp}`;
  })() : null;

  const scoreDisplay =
    isCompleted && state?.home_score !== undefined && state?.away_score !== undefined
      ? `${state.home_score}–${state.away_score}`
      : null;

  const carmelInitials = 'CFC';
  const opponentInitials = game.opponent.split(' ').map(w => w[0]).join('').slice(0, 3).toUpperCase();

  return (
    <div style={{
      background:   'rgba(255,255,255,0.03)',
      border:       `1px solid ${isLive ? `${NL_RED}88` : 'rgba(255,255,255,0.1)'}`,
      borderRadius: 16,
      overflow:     'hidden',
      transition:   'border-color 0.2s',
      boxShadow:    isLive
        ? `0 0 0 1px ${NL_RED}33, 0 4px 24px ${NL_RED}18`
        : '0 2px 12px rgba(0,0,0,0.2)',
    }}>

      {/* Top strip — colored by NL_NAVY with gold game label */}
      <div style={{
        background:     `linear-gradient(90deg, ${NL_NAVY} 0%, #162347 100%)`,
        padding:        '10px 18px',
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* NL Cup mini logo */}
          <img
            src={mediaUrl(NL_CUP_LOGO)}
            alt="NL Cup"
            style={{ width: 24, height: 24, objectFit: 'contain', flexShrink: 0 }}
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
          <span style={{
            fontSize:      11,
            fontWeight:    800,
            color:         NL_GOLD,
            letterSpacing: '1.2px',
            textTransform: 'uppercase' as const,
          }}>
            {isLive ? '🔴 ' : ''}{game.label}
            {isLive && (
              <span style={{ marginLeft: 8, fontSize: 9, color: '#EF4444', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 20, padding: '2px 7px', letterSpacing: '0.8px' }}>LIVE</span>
            )}
            {!loading && isCompleted && (
              <span style={{ marginLeft: 8, fontSize: 9, color: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 20, padding: '2px 7px', letterSpacing: '0.5px', fontWeight: 700 }}>
                FINAL{scoreDisplay ? ` · ${scoreDisplay}` : ''}
              </span>
            )}
          </span>
        </div>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', fontWeight: 600 }}>
          {game.date}
        </span>
      </div>

      {/* Matchup row */}
      <div style={{
        padding:        '20px 18px 16px',
        display:        'flex',
        alignItems:     'center',
      }}>
        {/* Carmel FC */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, flex: 1 }}>
          <TeamLogo
            src={CARMEL_LOGO}
            alt="Carmel FC"
            size={60}
            initials={carmelInitials}
            bgColor={`${NL_NAVY}55`}
            borderColor={`${NL_NAVY}99`}
          />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: NL_WHITE, lineHeight: 1.2 }}>Carmel FC</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontWeight: 600, marginTop: 1 }}>2013G Gold NL</div>
          </div>
        </div>

        {/* VS / score */}
        <div style={{
          flexShrink:    0,
          width:         52,
          display:       'flex',
          flexDirection: 'column',
          alignItems:    'center',
          gap:           4,
        }}>
          {isCompleted && scoreDisplay ? (
            <div style={{ fontSize: 18, fontWeight: 900, color: NL_WHITE, letterSpacing: '-1px', lineHeight: 1 }}>
              {scoreDisplay}
            </div>
          ) : (
            <>
              <div style={{ fontSize: 10, fontWeight: 900, color: NL_GOLD, letterSpacing: '1.5px' }}>VS</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontWeight: 600 }}>{game.time.split('/')[0].trim()}</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.2)', fontWeight: 600 }}>{game.time.split('/')[1]?.trim()}</div>
            </>
          )}
        </div>

        {/* Opponent */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, flex: 1 }}>
          <TeamLogo
            src={game.opponentLogo}
            alt={game.opponent}
            size={60}
            initials={opponentInitials}
            bgColor="rgba(255,255,255,0.05)"
            borderColor="rgba(255,255,255,0.12)"
          />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: NL_WHITE, lineHeight: 1.2 }}>{game.opponent}</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontWeight: 600, marginTop: 1 }}>{game.opponentSub}</div>
          </div>
        </div>
      </div>

      {/* Time row (below matchup, when not completed) */}
      {!isCompleted && (
        <div style={{ padding: '0 18px 4px', textAlign: 'center' }}>
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', fontWeight: 600 }}>
            ⏰ {game.time}
          </span>
        </div>
      )}

      {/* CTA row */}
      <div style={{
        padding:        '14px 18px 18px',
        display:        'flex',
        alignItems:     'center',
        gap:            10,
        borderTop:      '1px solid rgba(255,255,255,0.06)',
        marginTop:      4,
      }}>
        {!isCompleted && (
          <a
            href={`/live/${game.id}`}
            style={{
              display:        'inline-flex',
              alignItems:     'center',
              justifyContent: 'center',
              gap:            8,
              padding:        '11px 22px',
              background:     isLive ? NL_RED : NL_NAVY,
              border:         isLive ? 'none' : `1px solid ${NL_NAVY}`,
              borderRadius:   8,
              color:          NL_WHITE,
              fontWeight:     800,
              fontSize:       14,
              textDecoration: 'none',
              letterSpacing:  '-0.2px',
              whiteSpace:     'nowrap' as const,
              flex:           1,
              boxShadow:      isLive ? `0 2px 12px ${NL_RED}44` : 'none',
            }}
          >
            {isLive
              ? <><div style={{ width: 7, height: 7, borderRadius: '50%', background: 'rgba(255,255,255,0.9)', animation: 'pulse 1.5s infinite' }} /> Watch Live</>
              : <><svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polygon points="3,2 12,7 3,12"/></svg> Watch Stream</>
            }
          </a>
        )}

        {isCompleted && hasRecording && (
          <button
            onClick={() => setShowPlayer(p => !p)}
            style={{
              display:        'inline-flex',
              alignItems:     'center',
              justifyContent: 'center',
              gap:            8,
              padding:        '11px 22px',
              background:     showPlayer ? `${NL_NAVY}88` : NL_NAVY,
              border:         'none',
              borderRadius:   8,
              color:          NL_WHITE,
              fontWeight:     800,
              fontSize:       14,
              cursor:         'pointer',
              letterSpacing:  '-0.2px',
              whiteSpace:     'nowrap' as const,
              flex:           1,
              touchAction:    'manipulation',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="white"><path d="M3 2.5l9 4.5-9 4.5V2.5z"/></svg>
            {showPlayer ? 'Hide Replay' : 'Watch Replay'}
          </button>
        )}

        {isCompleted && !hasRecording && (
          <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.3)', fontStyle: 'italic', flex: 1, textAlign: 'center' }}>
            Replay uploading soon…
          </span>
        )}
      </div>

      {/* Inline video player */}
      {showPlayer && recordingUrl && (
        <div style={{ padding: '0 16px 16px' }}>
          <video
            src={recordingUrl}
            controls
            playsInline
            autoPlay
            style={{ width: '100%', borderRadius: 8, background: '#000', display: 'block', maxHeight: '70vw' }}
            onError={e => {
              const v = e.currentTarget;
              console.error('Video error:', v.error?.code, v.error?.message, recordingUrl);
            }}
          />
          <div style={{ marginTop: 8, fontSize: 11, color: 'rgba(255,255,255,0.25)', textAlign: 'center' }}>
            Full game recording · Carmel FC vs. {game.opponent}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const NLCupWatch: React.FC = () => (
  <div style={{
    minHeight:     '100vh',
    background:    '#080b14',
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'center',
    fontFamily:    'system-ui, -apple-system, sans-serif',
  }}>

    {/* ── Hero header ── */}
    <div style={{
      width:         '100%',
      background:    `linear-gradient(175deg, ${NL_NAVY} 0%, #111932 65%, #080b14 100%)`,
      borderBottom:  `3px solid ${NL_RED}`,
      display:       'flex',
      flexDirection: 'column',
      alignItems:    'center',
      padding:       '36px 24px 32px',
      boxSizing:     'border-box',
      position:      'relative',
      overflow:      'hidden',
    }}>
      {/* Subtle grid overlay */}
      <div style={{
        position:        'absolute',
        inset:           0,
        pointerEvents:   'none',
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)',
        backgroundSize:  '40px 40px',
      }} />

      {/* NL Cup logo — large and centered */}
      <img
        src={mediaUrl(NL_CUP_LOGO)}
        alt="The National League Cup"
        style={{
          width:     110,
          height:    110,
          objectFit: 'contain',
          position:  'relative',
          marginBottom: 18,
          filter:    'drop-shadow(0 4px 16px rgba(0,0,0,0.5))',
        }}
        onError={e => {
          // fallback to USYS NL logo
          (e.target as HTMLImageElement).src = mediaUrl(USYS_NL_LOGO);
        }}
      />

      {/* Title */}
      <div style={{ textAlign: 'center', position: 'relative', marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: NL_GOLD, letterSpacing: '3px', textTransform: 'uppercase' as const, marginBottom: 6 }}>
          The National League
        </div>
        <div style={{ fontSize: 28, fontWeight: 900, color: NL_WHITE, letterSpacing: '-0.8px', lineHeight: 1.05 }}>
          Cup Finals 2026
        </div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', marginTop: 6, fontWeight: 500 }}>
          Creve Coeur Soccer Park · St. Louis, MO
        </div>
      </div>

      {/* Red pill — National Championship */}
      <div style={{
        background:   NL_RED,
        borderRadius: 40,
        padding:      '6px 20px',
        marginBottom: 18,
        boxShadow:    `0 2px 12px ${NL_RED}55`,
      }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: NL_WHITE, letterSpacing: '2px', textTransform: 'uppercase' as const }}>
          ⚽ National Championship
        </span>
      </div>

      {/* Divider with Carmel FC identity */}
      <div style={{
        display:      'flex',
        alignItems:   'center',
        gap:          12,
        background:   'rgba(255,255,255,0.05)',
        border:       '1px solid rgba(255,255,255,0.1)',
        borderRadius: 50,
        padding:      '8px 18px',
      }}>
        <img
          src={mediaUrl(CARMEL_LOGO)}
          alt="Carmel FC"
          style={{ width: 28, height: 28, objectFit: 'contain', borderRadius: 6, background: 'rgba(255,255,255,0.1)' }}
          onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
        <span style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.75)', letterSpacing: '-0.2px' }}>
          Carmel FC 2013G Gold NL
        </span>
      </div>
    </div>

    {/* ── Game cards ── */}
    <div style={{ width: '100%', maxWidth: 560, padding: '28px 20px 8px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 2 }}>
        <h2 style={{
          margin: 0, fontSize: 11, fontWeight: 700,
          color: 'rgba(255,255,255,0.3)', letterSpacing: '2px', textTransform: 'uppercase' as const,
        }}>
          Championship Schedule
        </h2>
        <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
        <span style={{ fontSize: 11, color: NL_GOLD, fontWeight: 700 }}>July 7–9</span>
      </div>
      {GAMES.map((g, i) => (
        <GameCard key={g.id} game={g} index={i} />
      ))}
    </div>

    {/* ── USYS NL Logo + App Store + sign-up CTAs ── */}
    <div style={{ width: '100%', maxWidth: 560, padding: '28px 20px 44px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
      <div style={{ width: '100%', height: 1, background: 'rgba(255,255,255,0.07)' }} />

      {/* USYS NL League logo */}
      <img
        src={mediaUrl(USYS_NL_LOGO)}
        alt="USYS National League"
        style={{ width: 72, height: 'auto', objectFit: 'contain', opacity: 0.7 }}
        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
      />

      <p style={{ margin: 0, fontSize: 13, color: 'rgba(255,255,255,0.35)', textAlign: 'center' }}>
        Get the full experience — live scores, stats &amp; highlights
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'center', alignItems: 'center' }}>
        <AppStoreBadge />
        <a
          href="https://www.slowrib.com/register"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '10px 20px',
            background: `${NL_RED}22`, border: `1px solid ${NL_RED}66`,
            borderRadius: 8, color: '#ff6680', fontWeight: 700, fontSize: 14,
            textDecoration: 'none', whiteSpace: 'nowrap' as const,
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = `${NL_RED}33`; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = `${NL_RED}22`; }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="8" cy="5" r="3"/>
            <path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6"/>
          </svg>
          Create Free Account
        </a>
      </div>

      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.18)', textAlign: 'center', lineHeight: 1.7 }}>
        Powered by <a href="https://www.slowrib.com" style={{ color: 'rgba(255,255,255,0.3)', textDecoration: 'none', fontWeight: 600 }}>Slow Rib Sports</a>
        {' · '}
        <a href="https://www.slowrib.com/privacy" style={{ color: 'rgba(255,255,255,0.2)', textDecoration: 'none' }}>Privacy</a>
        {' · '}
        <a href="https://www.slowrib.com/terms" style={{ color: 'rgba(255,255,255,0.2)', textDecoration: 'none' }}>Terms</a>
      </div>
    </div>

    <style>{`
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50%       { opacity: 0.35; }
      }
    `}</style>
  </div>
);

export default NLCupWatch;
